/**
 * MSI / Plessey decoder.
 *
 * Input: a scanline of bar/space widths (in arbitrary units — pixels,
 * modules, whatever — as long as they're consistent within the
 * scanline). Output: the decoded digit string + check-digit validation
 * result, or null if no valid symbol was found.
 *
 * Algorithm:
 *   1. Slide a window across `widths` looking for the start pattern
 *      (one wide bar). At each candidate start position:
 *   2. Determine the local narrow/wide threshold from the next few
 *      bars — MSI is canonically 2:1 but real-world printings drift.
 *   3. Decode 4-bar+4-space chunks as BCD digits using DIGIT_PATTERNS.
 *      Stop on either: stop pattern hit, decoder hits a chunk that
 *      doesn't match any digit pattern (likely end of barcode), or
 *      maxDigits exceeded.
 *   4. For the candidate digit string, try each ChecksumVariant in
 *      order. First one that validates wins.
 *   5. Score the match by how regular the bar/space widths were
 *      (low std-dev within narrow group + wide group = high
 *      confidence).
 */

import { validateAndStrip } from './checksum';
import {
  DIGIT_PATTERNS,
  NARROW,
  WIDE,
} from './lookup';
import type {
  ChecksumVariant,
  MSIDecodeAttemptTrace,
  MSIDecodeOptions,
  MSIDecodeResult,
  MSIRejectReason,
  Scanline,
} from './types';

// Defaults are tuned for US retail shelf tags but lenient enough to
// survive real-world camera variance (paper texture, ink bleed,
// lighting, sub-pixel anti-aliasing). Initial pass at 1.7-2.5 / 0.5 /
// 5 was too strict and rejected legitimate Walgreens/CVS shelf tags;
// loosened to the values below pending real-photo tuning data from
// /diagnostics/scanner submissions.
//
// Callers can override per-call (test fixtures use minDigits:1 to
// exercise short synthetic round-trips).
const DEFAULT_OPTIONS: Required<MSIDecodeOptions> = {
  minDigits: 6,
  maxDigits: 16,
  // Field data from diagnostics submission #12 caught a real shelf tag
  // decoded as "0694347" across 3 scanlines × 2 captures, failing
  // mod10 — manual computation showed it validates as mod11. So the
  // retailer's encoder uses mod11, not mod10. Trying both keeps real
  // tags decoding; cross-scanline voting (>=2 matches) keeps the FP
  // rate bounded despite the extra variant.
  checksums: ['mod10', 'mod11'],
  wideRatioRange: [1.5, 3.0],
  minConfidence: 0.25,
  // Diagnostic submission #9 found dozens of legitimate-looking
  // candidates (ratio 2.2-2.5, moduleUnit ~5 px) rejected at quiet zone
  // ~1.8-2.5 modules. Dropping from 3 to 1.5 lets those through; the
  // checksum gate and (new) cross-scanline voting kill false positives.
  requiredLeadingQuietModules: 1.5,
  // #2205 / diag #55 — refuse cut-off partials. Measured to the FRAME edge, so
  // a centered tag has a huge margin and is unaffected; only a tag that runs
  // off the frame edge (the partial-read case) is rejected.
  requiredTrailingQuietModules: 1.5,
  dumpRunsForPromising: false,
};

/**
 * Try to decode a single scanline. Returns the best (highest-confidence)
 * valid decode, or null if none found.
 *
 * "Best" is defined as: longest data string first, then highest
 * confidence score. We prefer longer matches because random noise tends
 * to produce short spurious decodes; a 7-digit decode is overwhelmingly
 * more likely to be real than a 4-digit one.
 */
export function decodeScanline(
  scan: Scanline,
  options: MSIDecodeOptions = {},
): MSIDecodeResult | null {
  return decodeScanlineWithTraces(scan, options).best;
}

/**
 * Same as `decodeScanline` but also returns every attempt made (with
 * reason for rejection). Used by the diagnostics scanner page to expose
 * near-misses so support can see why a tag isn't decoding cleanly.
 */
export function decodeScanlineWithTraces(
  scan: Scanline,
  options: MSIDecodeOptions = {},
): { best: MSIDecodeResult | null; attempts: MSIDecodeAttemptTrace[] } {
  const opts: Required<MSIDecodeOptions> = { ...DEFAULT_OPTIONS, ...options };
  const widths = scan.widths;
  const attempts: MSIDecodeAttemptTrace[] = [];
  if (widths.length < 11) return { best: null, attempts };

  let best: MSIDecodeResult | null = null;

  for (let i = 0; i < widths.length; i++) {
    const elementIsBar = scan.firstIsBar ? i % 2 === 0 : i % 2 === 1;
    if (!elementIsBar) continue;

    const traceBuffer: MSIDecodeAttemptTrace[] = [];
    const xOffset = scan.xOffset ?? 0;
    // frameWidth==0 means "unknown" (bare/synthetic scanline) — the cut-off
    // (trailing-quiet) gate only applies when we have real frame context.
    const frameWidth = scan.frameWidth ?? 0;
    const attempt = tryDecodeFrom(widths, i, scan.firstIsBar, opts, scan.y, traceBuffer, xOffset, frameWidth);
    attempts.push(...traceBuffer);
    if (
      attempt &&
      (!best ||
        attempt.digits.length > best.digits.length ||
        (attempt.digits.length === best.digits.length && attempt.confidence > best.confidence))
    ) {
      best = attempt;
    }
  }

  return { best, attempts };
}

/**
 * Decode starting from a specific element index assumed to be the start
 * bar. Returns null if no clean MSI symbol decodes from here.
 */
function sumWidths(widths: number[], from = 0, to = widths.length): number {
  let s = 0;
  for (let i = from; i < to; i++) s += widths[i];
  return s;
}

/**
 * Split a run-width window into narrow vs wide cluster CENTERS via 1-D 2-means
 * (#2205, diag #57). Robust to a wide start bar / merged runs / noise that fool
 * raw min-max, so the narrow/wide threshold and ratio reflect the actual two
 * bar populations on a degraded real shelf tag.
 */
export function clusterWidths(window: number[]): { narrow: number; wide: number } {
  const vals = window.filter((w) => w > 0);
  if (vals.length === 0) return { narrow: 1, wide: 2 };
  let narrow = Math.min(...vals);
  let wide = Math.max(...vals);
  if (narrow === wide) return { narrow, wide };
  for (let it = 0; it < 12; it++) {
    const mid = (narrow + wide) / 2;
    let sN = 0;
    let nN = 0;
    let sW = 0;
    let nW = 0;
    for (const v of vals) {
      if (v <= mid) {
        sN += v;
        nN += 1;
      } else {
        sW += v;
        nW += 1;
      }
    }
    const nextN = nN ? sN / nN : narrow;
    const nextW = nW ? sW / nW : wide;
    if (nextN === narrow && nextW === wide) break;
    narrow = nextN;
    wide = nextW;
  }
  return { narrow, wide };
}

function tryDecodeFrom(
  widths: number[],
  startIdx: number,
  firstIsBar: boolean,
  opts: Required<MSIDecodeOptions>,
  scanlineY: number = 0,
  traces?: MSIDecodeAttemptTrace[],
  xOffset: number = 0,
  frameWidth: number = 0,
): MSIDecodeResult | null {
  // For "promising" rejections (past the ratio gate), optionally capture
  // the first ~30 raw widths so we can replay the decoder against actual
  // bar data offline. Gated by opts.dumpRunsForPromising so the hot path
  // doesn't bloat traces with thousands of arrays.
  const reject = (reason: MSIRejectReason, extra: Partial<MSIDecodeAttemptTrace> = {}): null => {
    if (traces) {
      const trace: MSIDecodeAttemptTrace = { scanlineY, startIdx, reason, ...extra };
      if (opts.dumpRunsForPromising && reason !== 'ratio_out_of_band') {
        const end = Math.min(widths.length, startIdx + 32);
        trace.runs = widths.slice(startIdx, end);
      }
      traces.push(trace);
    }
    return null;
  };

  if (startIdx >= widths.length) return null;
  const startBarWidth = widths[startIdx];

  const lookahead = Math.min(16, widths.length - startIdx);
  const window = widths.slice(startIdx, startIdx + lookahead);
  if (window.length < 9) return null;

  const minW = Math.min(...window);
  if (minW <= 0) return null;
  // #2205 (diag #57) — classify narrow vs wide by 2-means clustering the window
  // widths, NOT raw (min+max)/2. Real shelf tags are a degraded ~1.5-2:1 with a
  // wide start bar + noisy mid-range runs; raw min/max let the wide start bar (or
  // a merged run) skew both the ratio gate and the threshold, so the TRUE start
  // window misclassified everything as narrow → the decoder skipped to a later
  // start and DROPPED the leading digits (0106943 read as 069434). Cluster
  // centers are robust to those outliers.
  const { narrow, wide } = clusterWidths(window);
  const ratio = narrow > 0 ? wide / narrow : Infinity;
  const [lo, hi] = opts.wideRatioRange;
  if (ratio < lo || ratio > hi) {
    return reject('ratio_out_of_band', { ratio });
  }

  const threshold = (narrow + wide) / 2;
  const moduleUnit = narrow;

  // Was: `startBarWidth < threshold`. That gate misfired when the
  // lookahead happened to straddle a quiet zone, inflating maxW and
  // pushing the threshold well above any plausible "wide" bar. Real
  // MSI start bars are >= 2 modules; allow some lens-softness slack
  // by accepting >= 1.5 modules.
  if (startBarWidth < moduleUnit * 1.5) {
    return reject('start_bar_too_narrow', { ratio, threshold, moduleUnit });
  }

  if (opts.requiredLeadingQuietModules > 0 && startIdx > 0) {
    const prevWidth = widths[startIdx - 1];
    const prevModules = prevWidth / moduleUnit;
    if (prevModules < opts.requiredLeadingQuietModules) {
      return reject('quiet_zone_too_short', {
        ratio,
        threshold,
        moduleUnit,
        quietZoneModules: prevModules,
      });
    }
  }

  // Walk forward decoding digits. Element index pointer.
  let p = startIdx + 1;
  let elementIsBar = !isBarAt(p, firstIsBar);
  // After start bar, next element is a space. Encoder emits 1 narrow
  // intercharacter space here (see encoder.ts). The decoder accepts
  // either: a real narrow space, or no leading space at all (raw spec).
  // To handle the "no leading space" case, peek: if widths[p] is a bar,
  // we're already on digit-1's first bar.
  //
  // Detect by parity: at index p, is it a bar or space?
  if (p >= widths.length) return null;
  const pIsBar = isBarAt(p, firstIsBar);
  if (!pIsBar) {
    // Skip the leading intercharacter space.
    p += 1;
  }

  const digits: number[] = [];
  let totalModules = startBarWidth;

  while (digits.length < opts.maxDigits && p + 7 < widths.length) {
    // Each digit consumes 8 elements: bar, space, bar, space, bar, space, bar, space
    const chunk = widths.slice(p, p + 8);
    // Verify alternation: bar, space, bar, space, ...
    const startsWithBar = isBarAt(p, firstIsBar);
    if (!startsWithBar) break; // alignment off — stop here

    // A digit's 8 elements are each ~1-2 modules. A run far wider than a wide
    // bar means we've reached the stop / inter-symbol quiet zone — stop here so
    // we don't read a SPURIOUS trailing digit past the real stop (#2205 diag
    // #57: 0106943's mod10 check digit 4 was mis-stripped because the decoder
    // over-read one digit into the quiet zone, then stripped the wrong digit).
    if (chunk.some((w) => w > moduleUnit * 3.5)) break;

    // Check if this looks like the stop pattern (narrow bar + wide bar)
    // followed by no more decodable digits. The stop is 2 elements
    // (NARROW bar + WIDE bar) starting on a bar.
    if (p + 1 < widths.length) {
      const w0 = widths[p];
      const w1 = widths[p + 1];
      // STOP_PATTERN [NARROW, WIDE] - both BARS, no space between
      // (encoder appends them sequentially after the last digit's
      //  last space). So this is bar, BAR — but that breaks alternation!
      // In practice: after last digit's last space, we get one narrow
      // bar then one wide bar with NO space between them, OR the run-
      // length encoder collapsed them into one "narrow+wide" run...
      //
      // Simpler approach: treat ANY end-of-decode condition (insufficient
      // remaining widths OR next chunk doesn't match a digit) as the
      // stop. The check digit gives us validation; we don't NEED to
      // detect the stop pattern explicitly.
      if (w0 < threshold && w1 >= threshold && (p + 8 >= widths.length || !looksLikeDigit(widths, p, threshold))) {
        // Looks like trailing stop without a following digit. Accept.
        totalModules += w0 + w1;
        p += 2; // consume the stop so the end-pointer marks the true right edge
        break;
      }
    }

    const digit = decodeDigitChunk(chunk, threshold);
    if (digit === null) break;

    digits.push(digit);
    for (const w of chunk) totalModules += w;
    p += 8;
    elementIsBar = isBarAt(p, firstIsBar);
    void elementIsBar; // alternation tracked implicitly
  }

  const raw = digits.map((d) => d.toString()).join('');

  if (digits.length < opts.minDigits) {
    return reject('too_short', {
      ratio,
      threshold,
      moduleUnit,
      digitsDecoded: digits.length,
      rawDigits: raw,
    });
  }

  // Compute confidence once over the actual matched widths (start +
  // data digit chunks). Using too small a window or the whole scanline
  // both give misleading scores.
  const matchedElementCount = 1 + (looksLikeIntercharSpace(widths, startIdx) ? 1 : 0) + digits.length * 8;
  const matchedWindow = widths.slice(startIdx, startIdx + matchedElementCount);
  const confidence = scoreConfidence(matchedWindow, threshold);
  if (confidence < opts.minConfidence) {
    return reject('low_confidence', {
      ratio,
      threshold,
      moduleUnit,
      digitsDecoded: digits.length,
      rawDigits: raw,
      confidence,
    });
  }

  // Pixel-domain span of the matched symbol (#2205). x0 = left edge of the
  // start bar, x1 = right edge of the consumed run. Quiet zones are measured
  // as the gap from each symbol edge to the frame edge (in modules): ~0 on a
  // side means the tag ran off that edge = a cut-off partial read.
  const x0 = xOffset + sumWidths(widths, 0, startIdx);
  // True right edge = cumulative px at the end-pointer `p` (exact), rather
  // than x0 + totalModules which under-counts when the stop isn't fully tallied.
  const x1 = xOffset + sumWidths(widths, 0, Math.min(p, widths.length));
  const frameKnown = frameWidth > 0;
  // Quiet zones = the white margins between the row's content and each frame
  // edge, measured EXACTLY from the trim offsets (not the decode pointer):
  //   leading  = xOffset (white trimmed off the left)
  //   trailing = frameWidth - xOffset - sum(widths)  (white trimmed off the right)
  // A centered tag has huge margins; a cut-off tag has ~0 on the clipped side.
  const leadingQuietModules = moduleUnit > 0 ? xOffset / moduleUnit : 0;
  const trailingQuietModules =
    frameKnown && moduleUnit > 0
      ? Math.max(0, frameWidth - xOffset - sumWidths(widths)) / moduleUnit
      : undefined;
  const span = { x0, x1, y: scanlineY };

  // #2205 / diag #55 — reject a cut-off partial: the symbol ran to (or off)
  // the frame's right edge, so we can't trust the trailing digits weren't
  // truncated. Only enforced with real frame context; disable via opt for
  // telemetry-only mode.
  if (
    frameKnown &&
    opts.requiredTrailingQuietModules > 0 &&
    (trailingQuietModules ?? Infinity) < opts.requiredTrailingQuietModules
  ) {
    return reject('trailing_quiet_too_short', {
      ratio,
      threshold,
      moduleUnit,
      digitsDecoded: digits.length,
      rawDigits: raw,
      quietZoneModules: trailingQuietModules,
    });
  }

  // Try each checksum variant in preference order.
  for (const variant of opts.checksums) {
    const stripped = validateAndStrip(raw, variant);
    if (stripped !== null && stripped.length >= opts.minDigits - checkDigitCount(variant)) {
      if (traces) {
        traces.push({
          scanlineY,
          startIdx,
          reason: 'accepted',
          ratio,
          threshold,
          moduleUnit,
          digitsDecoded: digits.length,
          rawDigits: raw,
          confidence,
          note: `checksum=${variant} → ${stripped}`,
        });
      }
      return {
        digits: stripped,
        raw,
        checksum: variant,
        modules: Math.round(totalModules / moduleUnit),
        confidence,
        span,
        leadingQuietModules,
        trailingQuietModules,
      };
    }
  }

  return reject('checksum_failed', {
    ratio,
    threshold,
    moduleUnit,
    digitsDecoded: digits.length,
    rawDigits: raw,
    confidence,
    note: `tried ${opts.checksums.join(', ')}`,
  });
}

/**
 * Is element widths[startIdx+1] a space-shaped element (i.e., the
 * narrow intercharacter space between start bar and digit-1's first
 * bar)? Mirrors the encoder's emission shape so confidence scoring
 * stays accurate.
 */
function looksLikeIntercharSpace(widths: number[], startIdx: number): boolean {
  if (startIdx + 1 >= widths.length) return false;
  // We can't directly know "bar vs space" here without parity tracking,
  // but the caller (tryDecodeFrom) handles that. For confidence scoring
  // purposes this approximation is fine: any narrow element after the
  // start bar counts toward the matched-window length.
  return true;
}

/** Number of trailing check digits added by a given variant. */
function checkDigitCount(v: ChecksumVariant): number {
  switch (v) {
    case 'none':
      return 0;
    case 'mod10':
    case 'mod11':
      return 1;
    case 'mod1010':
    case 'mod1110':
      return 2;
  }
}

/**
 * Quickly tell whether the next 8 elements at position p look like they
 * could be a digit (4 bars + 4 spaces with reasonable widths).
 * Used to disambiguate "another digit follows" vs "we hit the stop".
 */
function looksLikeDigit(widths: number[], p: number, threshold: number): boolean {
  if (p + 7 >= widths.length) return false;
  // Every element in the next 8 should be either clearly narrow
  // (< threshold) or clearly wide (>= threshold). Random non-barcode
  // widths often have weird mid-range values.
  for (let i = 0; i < 8; i++) {
    const w = widths[p + i];
    if (w <= 0) return false;
  }
  return decodeDigitChunk(widths.slice(p, p + 8), threshold) !== null;
}

/**
 * Decode an 8-element chunk (4 bars + 4 spaces, alternating) into a
 * single digit 0..9 by comparing against DIGIT_PATTERNS.
 * Returns null if no pattern matches.
 */
function decodeDigitChunk(chunk: number[], threshold: number): number | null {
  if (chunk.length !== 8) return null;
  // Convert each element to its module class: narrow(1) or wide(2).
  const classes: number[] = chunk.map((w) => (w < threshold ? NARROW : WIDE));
  for (let d = 0; d < 10; d++) {
    const pat = DIGIT_PATTERNS[d];
    let match = true;
    for (let i = 0; i < 8; i++) {
      if (classes[i] !== pat[i]) {
        match = false;
        break;
      }
    }
    if (match) return d;
  }
  return null;
}

function isBarAt(idx: number, firstIsBar: boolean): boolean {
  return firstIsBar ? idx % 2 === 0 : idx % 2 === 1;
}

/**
 * Confidence in 0..1 based on how cleanly the bar widths cluster into
 * narrow vs wide groups. A symbol with perfectly uniform narrow bars
 * and perfectly uniform wide bars scores high; one where narrow varies
 * 8..14 and wide varies 16..28 scores low (printing/lighting variance).
 */
function scoreConfidence(window: number[], threshold: number): number {
  if (window.length === 0) return 0;
  const narrows = window.filter((w) => w < threshold);
  const wides = window.filter((w) => w >= threshold);
  if (narrows.length === 0 || wides.length === 0) return 0;
  const cv = (arr: number[]): number => {
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    if (mean <= 0) return 1;
    const variance = arr.reduce((s, x) => s + (x - mean) * (x - mean), 0) / arr.length;
    return Math.sqrt(variance) / mean;
  };
  const cvN = cv(narrows);
  const cvW = cv(wides);
  // Average coefficient of variation; clamp and invert.
  const avgCv = (cvN + cvW) / 2;
  return Math.max(0, Math.min(1, 1 - avgCv * 2));
}
